/**
 * Swedish accounting categories for expense classification
 * Based on K2/K3 accounting standards
 *
 * NOTE: Keep in sync with backend constants at:
 * supabase/functions/_shared/constants/expenseCategories.ts
 */

export const EXPENSE_CATEGORIES = {
  FEES: 'fees',
  COFFEE_OTHER_PERSONNEL: 'coffee_other_personnel',
  WELLNESS: 'wellness',
  CONSUMABLE_INVENTORY: 'consumable_inventory',
  CONSUMABLE_MATERIALS: 'consumable_materials',
  IT_SERVICES: 'it_services',
  CARD_FEES: 'card_fees',
  OFFICE_SUPPLIES: 'office_supplies',
  FOOD_ACCOMMODATION: 'food_accommodation',
  CUSTOMER_EVENT: 'customer_event',
  CUSTOMER_REPRESENTATION: 'customer_representation',
  MOBILE_SUBSCRIPTION: 'mobile_subscription',
  STAFF_REPRESENTATION: 'staff_representation',
  POST_STAMPS: 'post_stamps',
  SOFTWARE: 'software',
  ADVERTISING_PR: 'advertising_pr',
  TRAVEL_COSTS: 'travel_costs',
  CONGESTION_TAX: 'congestion_tax',
  EDUCATION: 'education',
  PASS_THROUGH_BILLING: 'pass_through_billing',
  OTHER: 'other',
} as const;

export type ExpenseCategory = typeof EXPENSE_CATEGORIES[keyof typeof EXPENSE_CATEGORIES];

/** Category-specific requirements. Extend this union as new requirements are added. */
export type CategoryRequirement = 'participants';

/**
 * Display names and descriptions for Swedish expense categories
 */
export const EXPENSE_CATEGORY_INFO: Record<ExpenseCategory, {
  label: string;
  description: string;
  requirements?: CategoryRequirement[];
}> = {
  [EXPENSE_CATEGORIES.FEES]: {
    label: 'Avgifter',
    description: 'Various fees',
  },
  [EXPENSE_CATEGORIES.COFFEE_OTHER_PERSONNEL]: {
    label: 'Fikautlägg / Övriga personalkostnader',
    description: 'Coffee and snacks for personnel',
  },
  [EXPENSE_CATEGORIES.WELLNESS]: {
    label: 'Friskvård',
    description: 'Gymkort, godkända aktiviteter från skatteverket',
  },
  [EXPENSE_CATEGORIES.CONSUMABLE_INVENTORY]: {
    label: 'Förbrukningsinventarier',
    description: 'Livslängd < 3 år, datorer, skärmar, mobil, ipad, stolar, bord',
  },
  [EXPENSE_CATEGORIES.CONSUMABLE_MATERIALS]: {
    label: 'Förbrukningsmateriel',
    description: 'Livslängd < 1 år av mindre värde, ex glas, tallrikar, glödlampor, batterier, sladdar',
  },
  [EXPENSE_CATEGORIES.IT_SERVICES]: {
    label: 'IT-tjänster',
    description: 'IT services',
  },
  [EXPENSE_CATEGORIES.CARD_FEES]: {
    label: 'Kontokortsavgifter',
    description: 'Årsavgifter, faktureringsavgifter, kontantuttagsavgifter, dröjsmålsränta med mera',
  },
  [EXPENSE_CATEGORIES.OFFICE_SUPPLIES]: {
    label: 'Kontorsmaterial m.m.',
    description: 'Toner, papper, pennor',
  },
  [EXPENSE_CATEGORIES.FOOD_ACCOMMODATION]: {
    label: 'Kost och logi',
    description: 'Hotell vid tjänsteresa',
  },
  [EXPENSE_CATEGORIES.CUSTOMER_EVENT]: {
    label: 'Kundevent',
    description: 'Snacks och dricka, lättare förtäring',
  },
  [EXPENSE_CATEGORIES.CUSTOMER_REPRESENTATION]: {
    label: 'Kundrepresentation',
    description: 'Mat och dryck med kunder, leverantörer',
    requirements: ['participants'],
  },
  [EXPENSE_CATEGORIES.MOBILE_SUBSCRIPTION]: {
    label: 'Mobilabonnemang',
    description: 'Mobile subscription',
  },
  [EXPENSE_CATEGORIES.STAFF_REPRESENTATION]: {
    label: 'Personalrepresentation',
    description: 'Mat och dryck med anställda',
    requirements: ['participants'],
  },
  [EXPENSE_CATEGORIES.POST_STAMPS]: {
    label: 'Post & frimärken',
    description: 'Post and stamps',
  },
  [EXPENSE_CATEGORIES.SOFTWARE]: {
    label: 'Programvaror',
    description: 'Software',
  },
  [EXPENSE_CATEGORIES.ADVERTISING_PR]: {
    label: 'Reklam & PR',
    description: 'Advertising and PR',
  },
  [EXPENSE_CATEGORIES.TRAVEL_COSTS]: {
    label: 'Resekostnader',
    description: 'Taxi, hyrbil, flyg- och tågbiljetter, parkering',
  },
  [EXPENSE_CATEGORIES.CONGESTION_TAX]: {
    label: 'Trängselskatt, skattefri',
    description: 'Trängselskatt i samband med tjänsteresa med egen bil',
  },
  [EXPENSE_CATEGORIES.EDUCATION]: {
    label: 'Utbildning',
    description: 'Education and training',
  },
  [EXPENSE_CATEGORIES.PASS_THROUGH_BILLING]: {
    label: 'Vidarefakturering',
    description: 'Kostnader som ska vidarefaktureras med påslag',
  },
  [EXPENSE_CATEGORIES.OTHER]: {
    label: 'Övrigt',
    description: 'Other expenses',
  },
};

/**
 * Get all valid expense category values
 */
export function getValidExpenseCategories(): ExpenseCategory[] {
  return Object.values(EXPENSE_CATEGORIES);
}

/**
 * Get sorted category options for display in dropdown
 * Sorted alphabetically by Swedish label
 */
export function getExpenseCategoryOptions(): Array<{ value: ExpenseCategory; label: string; description: string }> {
  return Object.entries(EXPENSE_CATEGORY_INFO)
    .map(([value, info]) => ({
      value: value as ExpenseCategory,
      label: info.label,
      description: info.description,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
}

/**
 * Check if a value is a valid expense category
 */
export function isValidExpenseCategory(value: string): value is ExpenseCategory {
  return getValidExpenseCategories().includes(value as ExpenseCategory);
}

/**
 * Whether a given category requires the specified extra data.
 * Returns false for null/undefined/unknown categories.
 */
export function categoryRequires(
  category: string | null | undefined,
  req: CategoryRequirement,
): boolean {
  if (!category || !isValidExpenseCategory(category)) return false;
  return !!EXPENSE_CATEGORY_INFO[category].requirements?.includes(req);
}
